import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, ArrowRight, ArrowLeftRight, Plus, Trash2, 
  Save, RefreshCw, Check, AlertCircle, Copy, Loader2
} from 'lucide-react';
import { Button } from '../ui/Button';
import { fetchFieldMappings, saveFieldMappings } from '../../lib/crm/service';
import { FieldMapping } from '../../lib/crm/types';
import { getCrmProvider } from '../../lib/crm/providers';
import toast from 'react-hot-toast';

interface CrmFieldMappingEditorProps {
  connectionId: string;
  onClose: () => void;
}

interface MappingField {
  id: string;
  sourceField: string;
  targetField: string;
  direction: 'inbound' | 'outbound' | 'bidirectional';
  transformation?: string;
  enabled: boolean;
}

const CrmFieldMappingEditor: React.FC<CrmFieldMappingEditorProps> = ({ connectionId, onClose }) => {
  const [mappings, setMappings] = useState<MappingField[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'contacts' | 'companies' | 'opportunities' | 'activities'>('contacts');
  
  // Load field mappings on component mount
  useEffect(() => {
    loadFieldMappings();
  }, [connectionId]);
  
  const loadFieldMappings = async () => {
    try {
      setIsLoading(true);
      
      // In a real implementation, this would fetch field mappings from the database
      // For this demo, we'll generate some sample mappings
      
      // Fetch existing mappings if available
      try {
        const existingMappings = await fetchFieldMappings(connectionId);
        if (existingMappings.length > 0) {
          setMappings(existingMappings.map(mapping => ({
            id: mapping.id,
            sourceField: mapping.sourceField,
            targetField: mapping.targetField,
            direction: mapping.direction,
            transformation: mapping.transformation,
            enabled: mapping.enabled
          })));
          return;
        }
      } catch (error) {
        console.error('Error fetching field mappings:', error);
        // Continue with default mappings
      }
      
      // Generate default mappings based on provider
      const defaultMappings: MappingField[] = [
        {
          id: crypto.randomUUID(),
          sourceField: 'first_name',
          targetField: 'FirstName',
          direction: 'bidirectional',
          enabled: true
        },
        {
          id: crypto.randomUUID(),
          sourceField: 'last_name',
          targetField: 'LastName',
          direction: 'bidirectional',
          enabled: true
        },
        {
          id: crypto.randomUUID(),
          sourceField: 'email',
          targetField: 'Email',
          direction: 'bidirectional',
          enabled: true
        },
        {
          id: crypto.randomUUID(),
          sourceField: 'phone',
          targetField: 'Phone',
          direction: 'bidirectional',
          enabled: true
        },
        {
          id: crypto.randomUUID(),
          sourceField: 'address',
          targetField: 'Address',
          direction: 'bidirectional',
          enabled: true
        },
        {
          id: crypto.randomUUID(),
          sourceField: 'city',
          targetField: 'City',
          direction: 'bidirectional',
          enabled: true
        },
        {
          id: crypto.randomUUID(),
          sourceField: 'state',
          targetField: 'State',
          direction: 'bidirectional',
          enabled: true
        },
        {
          id: crypto.randomUUID(),
          sourceField: 'zip',
          targetField: 'Zip',
          direction: 'bidirectional',
          enabled: true
        }
      ];
      
      setMappings(defaultMappings);
    } catch (error) {
      console.error('Error loading field mappings:', error);
      toast.error('Failed to load field mappings');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleAddMapping = () => {
    const newMapping: MappingField = {
      id: crypto.randomUUID(),
      sourceField: '',
      targetField: '',
      direction: 'bidirectional',
      enabled: true
    };
    
    setMappings([...mappings, newMapping]);
  };
  
  const handleRemoveMapping = (id: string) => {
    setMappings(mappings.filter(mapping => mapping.id !== id));
  };
  
  const handleMappingChange = (id: string, field: keyof MappingField, value: any) => {
    setMappings(mappings.map(mapping => 
      mapping.id === id ? { ...mapping, [field]: value } : mapping
    ));
  };
  
  const handleSaveMappings = async () => {
    try {
      setIsSaving(true);
      
      // Validate mappings
      const invalidMappings = mappings.filter(mapping => 
        !mapping.sourceField || !mapping.targetField
      );
      
      if (invalidMappings.length > 0) {
        toast.error('Please fill in all required fields');
        return;
      }
      
      // Save mappings
      await saveFieldMappings(connectionId, mappings.map(mapping => ({
        sourceField: mapping.sourceField,
        targetField: mapping.targetField,
        direction: mapping.direction,
        transformation: mapping.transformation,
        enabled: mapping.enabled
      })));
      
      toast.success('Field mappings saved successfully');
      onClose();
    } catch (error) {
      console.error('Error saving field mappings:', error);
      toast.error('Failed to save field mappings');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleCopyMappings = () => {
    const mappingText = mappings.map(mapping => 
      `${mapping.sourceField} -> ${mapping.targetField} (${mapping.direction})`
    ).join('\n');
    
    navigator.clipboard.writeText(mappingText)
      .then(() => toast.success('Mappings copied to clipboard'))
      .catch(() => toast.error('Failed to copy mappings'));
  };
  
  const getDirectionIcon = (direction: string) => {
    switch (direction) {
      case 'inbound':
        return <ArrowLeft className="h-5 w-5" />;
      case 'outbound':
        return <ArrowRight className="h-5 w-5" />;
      case 'bidirectional':
        return <ArrowLeftRight className="h-5 w-5" />;
      default:
        return null;
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Field Mapping Configuration</h3>
          <p className="text-sm text-gray-500">
            Configure how fields are mapped between your CRM and AdvisorGrow
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyMappings}
            className="py-1 px-2"
          >
            <Copy className="h-4 w-4 mr-1" />
            Copy
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={loadFieldMappings}
            className="py-1 px-2"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Reset
          </Button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('contacts')}
            className={`${
              activeTab === 'contacts'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Contacts
          </button>
          <button
            onClick={() => setActiveTab('companies')}
            className={`${
              activeTab === 'companies'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Companies
          </button>
          <button
            onClick={() => setActiveTab('opportunities')}
            className={`${
              activeTab === 'opportunities'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Opportunities
          </button>
          <button
            onClick={() => setActiveTab('activities')}
            className={`${
              activeTab === 'activities'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Activities
          </button>
        </nav>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 mr-2" />
              <div>
                <p className="text-sm text-blue-700">
                  Field mappings determine how data is synchronized between AdvisorGrow and your CRM system.
                  Map each field in AdvisorGrow to its corresponding field in your CRM.
                </p>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AdvisorGrow Field</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Direction</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CRM Field</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Enabled</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mappings.map((mapping) => (
                  <tr key={mapping.id}>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <input
                        type="text"
                        value={mapping.sourceField}
                        onChange={(e) => handleMappingChange(mapping.id, 'sourceField', e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        placeholder="AdvisorGrow Field"
                      />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <select
                        value={mapping.direction}
                        onChange={(e) => handleMappingChange(mapping.id, 'direction', e.target.value)}
                        className="rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                      >
                        <option value="bidirectional">↔️ Both Ways</option>
                        <option value="inbound">← To AdvisorGrow</option>
                        <option value="outbound">→ To CRM</option>
                      </select>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <input
                        type="text"
                        value={mapping.targetField}
                        onChange={(e) => handleMappingChange(mapping.id, 'targetField', e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        placeholder="CRM Field"
                      />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <input
                        type="checkbox"
                        checked={mapping.enabled}
                        onChange={(e) => handleMappingChange(mapping.id, 'enabled', e.target.checked)}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleRemoveMapping(mapping.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={handleAddMapping}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Mapping
            </Button>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveMappings}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Mappings
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmFieldMappingEditor;